// @flow
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { NumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../../../components/mui/app/formBoxTitle/formBoxTitle";
import PageContentWrapper from "../../../../components/mui/app/pageContentWrapper/pageContentWrapper";
import QuickViewInfoWrap from "../../../../components/mui/app/quickViewInfoWrap/quickViewInfoWrap";
import SkeletonLoaderWrapper from "../../../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors } from "../../../../helpers/errorHandling";
import useUserData from "../../../../hooks/user/useUserData";
import { ISSUES_FORM } from "../../locales/namespaces";
import {DataGridPremium, GridActionsCellItem} from "@mui/x-data-grid-premium";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {muiGridLocales} from "../../../../helpers/mui/muiLocales";
import Typography from "@mui/material/Typography";
import TruncateDescription from "../../../../pages/dashboard/partials/truncateDescription";
import {numericFormatDigitDefaults} from "../../../../helpers/theme/defaultProps";
import {mapFilters} from "../../pages/index";
import {LoadingButton} from "@mui/lab";
import CircularProgress from "@mui/material/CircularProgress";
import EmailPreviewDialog from "../../../../components/mui/general/emailPreviewDialog/emailPreviewDialog";

type Props = {};

const PoQuickView = (props: Props) => {
    const {
        Code,
        onClose,
        loadingIndex,
        filters,
        dataManager,
        setRowSelectionModel,
        isFromReport = false,
    } = props
    const { t } = useTranslation(ISSUES_FORM)
    const { token,thousandSeparator,decimalSeparator,language, dateFormat, timeFormat } = useUserData()
    const [po, setPo] = useState([])
    const [loadingDelete,setLoadingDelete] = useState(false)
    const [loadingSend,setLoadingSend] = useState(false)
    const [selectedIds, setSelectedIds] = useState([]);
    const [description,setDescription] = useState('');
    const [erpCode,setErpCode] = useState('');
    const [locked,setLocked] = useState(false);
    const[rows, setRows] = useState([]);
    const [savingRowIds, setSavingRowIds] = useState([]);
    const [emailHistory, setEmailHistory] = useState([]);
    const [emailOpen, setEmailOpen] = useState(false);
    const [loadingEmailData, setLoadingEmailData] = useState(false);
    const [loadingSendEmail, setLoadingSendEmail] = useState(false);
    const [emailData, setEmailData] = useState({
        MsgId: '',
        Subject: '',
        to: '',
        cc: '',
        Body: '',
        isPreview: '0',
    });
    const addSavingRow = (rowId) => {
        setSavingRowIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
    };

    const removeSavingRow = (rowId) => {
        setSavingRowIds((prev) => prev.filter((id) => id !== rowId));
    };

    useEffect(() => {
        if (Code) {
            axios({
                method: 'post',
                url: API + '/en/custom/purchase/workOrder/quickview',
                data:{
                    Code:Code
                },
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            }).then(res => {
                setPo(res.data.data)
                setRows(res.data.data)
                setDescription(res.data.description)
                setErpCode(res.data.ErpCode)
                setLocked(res.data.locked)
                setEmailHistory(res.data.history || [])
                console.log('data',res.data.data)
            }).catch(error => {
                enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                    variant: 'error',
                    autoHideDuration: 3000,
                })
            })
        }
    }, [Code, t,])

    const totalChargeCost = React.useMemo(() => {
        return (rows || []).reduce((sum, row) => {
            return sum + (Number(row.ChargeCost) || 0);
        }, 0);
    }, [rows]);

    const totalNewCost = React.useMemo(() => {
        return (rows || []).reduce((sum, row) => {
            return sum + (Number(row.NewCost) || 0);
        }, 0);
    }, [rows]);

    const totalRow = useMemo(() => ({
        id:"",
        Code: t("Total"),
        Description: "",
        CustName: "",
        Address: "",
        ChargeCost: totalChargeCost,
        NewCost: totalNewCost,
    }), [totalChargeCost,totalNewCost, t]);

    const columns = useMemo(() => [
        {
            field: 'Code',
            headerName: t('Code'),
            description: t('Code'),
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                const isTotal = params.value === t("Total");
                return (
                    <Typography fontWeight={isTotal ? 700 : 400}>
                        {params.value}
                    </Typography>
                );
            }
        },
        {
            field: 'Description',
            headerName: t('Description'),
            description: t('Description'),
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'CustName',
            headerName: t('Customer'),
            description: t('Customer'),
            minWidth: 250,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'Site',
            headerName: t('Site'),
            description: t('Site'),
            minWidth: 250,
            flex: 1,
            renderCell: (params) => {
                return <TruncateDescription value={params.value}/>
            },
        },
        {
            field: 'ChargeCost',
            headerName: t('Subcontractor Charge'),
            description: t('Subcontractor Charge'),
            minWidth: 100,
            type: 'number',
            flex: 1,
            valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
            renderCell: (params) => {
                const isTotal = params.row.Code === t("Total");

                return (
                    <NumericFormat
                        {...numericFormatDigitDefaults}
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={decimalSeparator}
                        displayType="text"
                        value={params.value}
                        style={{ fontWeight: isTotal ? 700 : 400 }}
                    />
                );
            }

        },
        {
            field: 'NewCost',
            headerName: t('New Charge'),
            description: t('New Charge'),
            minWidth: 140,
            type: 'number',
            flex: 1,
            editable: !locked,
            valueGetter: (value) => value ? Number(value) : 0,
            valueParser: (value) => {
                if (value == null || value === '') return 0;
                return Number(String(value).replace(',', '.')) || 0;
            },
            renderCell: (params) => {
                const isTotal = params.row.Code === t("Total");
                const rowId = params.row.id ?? params.row.Code;
                const isSaving = savingRowIds.includes(rowId);

                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                        <NumericFormat
                            {...numericFormatDigitDefaults}
                            thousandSeparator={thousandSeparator}
                            decimalSeparator={decimalSeparator}
                            displayType="text"
                            value={params.value}
                            style={{ fontWeight: isTotal ? 700 : 400 }}
                        />
                        {isSaving && !isTotal && <CircularProgress size={14} />}
                    </Box>
                );
            }
        }

    ], [t])

    const handleDeletePO = () => {
        setLoadingDelete(true)
        axios({
            method: 'post',
            data: {
                Code:Code,
                issues: selectedIds,

            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: `${API}/en/custom/purchase/workOrder/remove`
        }).then((res) => {
            const onSuccess = () => {
                setRowSelectionModel([]);
                onClose()
                enqueueSnackbar(t('P.O deleted successfully!'), {
                    variant: 'success',
                    autoHideDuration: 3000,
                })
                setLoadingDelete(false)
            }
            const params =  { ...mapFilters(filters) };
            dataManager(params, onSuccess)
        }).catch((error) => {
            setLoadingDelete(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })

        })
    }

    const finalizeSendFlow = () => {
        const onSuccess = () => {
            setRowSelectionModel([]);
            onClose();
            enqueueSnackbar(
                t('P.O send successfully!'),
                {
                    variant: 'success',
                    autoHideDuration: 3000,
                }
            );
        };
        const params = { ...mapFilters(filters) };
        dataManager(params, onSuccess);
    };

    const fetchEmailData = (msgId) => {
        setLoadingEmailData(true);
        axios({
            method: 'post',
            data: {
                MsgId: msgId,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            url: `${API}/en/custom/purchase/workOrder/EmailData`
        })
            .then((res) => {
                const item = res.data?.data?.[0] || {};
                setEmailData({
                    MsgId: item.MsgId ?? msgId,
                    Subject: item.Subject ?? '',
                    to: item.to ?? '',
                    cc: item.cc ?? '',
                    Body: item.Body ?? '',
                    isPreview: item.isPreview ?? '0',
                });
                setLoadingEmailData(false);
                setLoadingSend(false);
                setEmailOpen(true);
            })
            .catch((error) => {
                setLoadingEmailData(false);
                setLoadingSend(false);
                enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                    variant: 'error',
                    autoHideDuration: 3000,
                });

            });
    };

    const handlePreviewHistoryEmail = (msgId) => {
        fetchEmailData(msgId);
    };

    const historyColumns = useMemo(() => [
        {
            field: 'MsgId',
            headerName: t('Message Code'),
            description: t('Message Code'),
            minWidth: 100,
            flex: 1,
        },
        {
            field: 'Subject',
            headerName: t('Subject'),
            description: t('Subject'),
            minWidth: 160,
            flex: 1.5,
            renderCell: (params) => <TruncateDescription value={params.value}/>,
        },
        {
            field: 'TO',
            headerName: t('To'),
            description: t('To'),
            minWidth: 200,
            flex: 1.5,
            renderCell: (params) => <TruncateDescription value={params.value}/>,
        },
        {
            field: 'CC',
            headerName: t('Cc'),
            description: t('Cc'),
            minWidth: 160,
            flex: 1,
            renderCell: (params) => <TruncateDescription value={params.value || '-'}/>,
        },
        {
            field: 'CreatedBy',
            headerName: t('Creator'),
            description: t('Creator'),
            minWidth: 120,
            flex: 1,
        },
        {
            field: 'CreatedAt',
            headerName: t('Created At'),
            description: t('Created At'),
            minWidth: 160,
            flex: 1,
            type: 'date',
            valueFormatter: (value, row, column, apiRef) => {
                return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
            }
        },
        {
            field: 'SendDt',
            headerName: t('Send Date'),
            description: t('Send Date'),
            minWidth: 160,
            flex: 1,
            type: 'date',
            valueFormatter: (value, row, column, apiRef) => {
                return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Preview'),
            width: 80,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<VisibilityIcon/>}
                    label={t('Preview')}
                    onClick={() => handlePreviewHistoryEmail(params.row.MsgId)}
                    color={'info'}
                />,
            ],
        },
    ], [t, handlePreviewHistoryEmail])

    const handleSend = () => {
        const translateErrorMessage = (message) => {
            const translations = {
                'missing Dimension!': t('missing Dimension!'),
                'missing Item!': t('missing Item!'),
                'missing Item and Dimension!': t('missing Item and Dimension!'),
            };
            return message.split('\n').map((line) => {
                const parts = line.split(' ');
                const code = parts.shift();
                const text = parts.join(' ');
                return `${code} ${translations[text] || text}`;
            });
        };
        setLoadingSend(true);
        axios({
            method: 'post',
            data: {
                Code: Code,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            url: `${API}/en/custom/purchase/transfer`
        })
            .then((res) => {
                const msgId = res.data?.MsgId;
                if (msgId) {
                    fetchEmailData(msgId);
                } else {
                    setLoadingSend(false);
                    finalizeSendFlow();
                }
            })
            .catch((error) => {
                setLoadingSend(false);
                const backendMessage = error?.response?.data?.message;
                const formattedErrors = backendMessage
                    ? translateErrorMessage(backendMessage)
                    : [t('An error occurred!')];
                enqueueSnackbar(
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                        {formattedErrors.map((msg, index) => (
                            <li key={index}>
                                {msg}
                            </li>
                        ))}
                    </ul>,
                    {
                        variant: 'error',
                        autoHideDuration: 6000,
                    }
                );
            });
    };

    const handleSendEmail = (values) => {
        setLoadingSendEmail(true);
        axios({
            method: 'post',
            data: {
                MsgId: emailData.MsgId,
                Subject: values.Subject,
                to: values.to,
                cc: values.cc?.trim() ? values.cc : null,
                Body: values.Body,
            },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            url: `${API}/en/custom/purchase/workOrder/email/send`
        })
            .then(() => {
                setLoadingSendEmail(false);
                setEmailOpen(false);
                finalizeSendFlow();
            })
            .catch((error) => {
                setLoadingSendEmail(false);
                enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            });
    };

    const handleCancelEmail = () => {
        setEmailOpen(false);
    };

    return (
        <>
        <SkeletonLoaderWrapper loading={!Boolean(po)}>
            <Box
                sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: "wrap-reverse",
                    gap: 0.2,
                    pt:2,
                    pr:2
                }}>
                    <IconButton
                        onClick={onClose}
                        color={'error'}
                        size={'small'}
                        sx={{ marginLeft: 'auto' }}
                    >
                        <ClearIcon/>
                    </IconButton>

                </Box>
                <PageContentWrapper>
                    <Box>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <FormBoxTitle
                                    title={t('General Information P.O')}
                                />
                            </Grid>
                            <Grid size={{ xs: 12}}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 2,
                                        mb: 3,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: "1 1 400px",
                                            minWidth: 0,
                                        }}
                                    >
                                        <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                                            <strong>{t("Description")}:</strong> {description}
                                        </Typography>
                                        <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                                            <strong>{t("ErpCode")}:</strong> {erpCode || '-'}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            justifyContent: "flex-end",
                                            alignItems: "center",
                                            gap: 1.5,
                                            flex: "0 1 auto",
                                        }}
                                    >
                                        {!isFromReport &&
                                            <>
                                                <LoadingButton
                                                    disabled={locked ||selectedIds.length === 0 }
                                                    loading={loadingDelete || loadingIndex}
                                                    variant={selectedIds.length === 0 ? "outlined" : "contained"}
                                                    color="error"
                                                    onClick={handleDeletePO}
                                                    sx={{
                                                        "&.Mui-disabled": {
                                                            borderColor: "#d32f2f",
                                                            color: "#d32f2f",
                                                        }
                                                    }}
                                                >
                                                    {t("Delete selected rows")}
                                                </LoadingButton>
                                                <LoadingButton
                                                    loading={loadingDelete || loadingIndex}
                                                    variant={selectedIds.length > 0 ? "outlined" : "contained"}
                                                    color="error"
                                                    disabled={locked||selectedIds.length > 0 }
                                                    onClick={handleDeletePO}
                                                    sx={{
                                                        "&.Mui-disabled": {
                                                            borderColor: "#d32f2f",
                                                            color: "#d32f2f",
                                                        }
                                                    }}
                                                >
                                                    {t("Delete P.O")}
                                                </LoadingButton>
                                            </>}
                                        <LoadingButton
                                            disabled={locked}
                                            loading={loadingSend || loadingIndex}
                                            variant={"contained"}
                                            color="warning"
                                            onClick={handleSend}
                                            sx={{
                                                "&.Mui-disabled": {
                                                    borderColor: "#ebb434",
                                                    color: "#ebc934",
                                                }
                                            }}
                                        >
                                            {t("Send")}
                                        </LoadingButton>
                                    </Box>
                                </Box>
                                <Box sx={{width:'93%',ml:2}}>
                                    <DataGridPremium
                                        rows={rows}
                                        columns={columns}
                                        //getRowId={(row)=>row.id}
                                        pinnedRows={{
                                            bottom: [totalRow],
                                        }}
                                        {...((!isFromReport || !locked) && {
                                            checkboxSelection: true,
                                            rowSelectionModel: selectedIds,
                                            onRowSelectionModelChange: (newSelection) => setSelectedIds(newSelection),
                                            isRowSelectable: (params) => params.row.Code !== t("Total"),
                                        })}
                                        processRowUpdate={(newRow, oldRow) => {
                                            const updatedRow = {
                                                ...newRow,
                                                NewCost: Number(newRow.NewCost) || 0,
                                            };

                                            const oldValue = Number(oldRow.NewCost) || 0;
                                            const newValue = Number(updatedRow.NewCost) || 0;
                                            const rowId = updatedRow.id ?? updatedRow.Code;

                                            if (oldValue === newValue) {
                                                return updatedRow;
                                            }

                                            addSavingRow(rowId);

                                            return axios({
                                                method: "post",
                                                url: `${API}/en/custom/purchase/workOrder/update/row`,
                                                data: {
                                                    id: rowId,
                                                    value: updatedRow.NewCost,
                                                    code: Code,
                                                },
                                                headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${token}`,
                                                },
                                            })
                                                .then(() => {
                                                    setRows((prev) =>
                                                        prev.map((row) =>
                                                            (row.id ?? row.Code) === rowId ? updatedRow : row
                                                        )
                                                    );

                                                    enqueueSnackbar(t("Saved successfully!"), {
                                                        variant: "success",
                                                        autoHideDuration: 2000,
                                                    });

                                                    removeSavingRow(rowId);
                                                    return updatedRow;
                                                })
                                                .catch((error) => {
                                                    enqueueSnackbar(handleAxiosErrors(error, t("An error occurred!")), {
                                                        variant: "error",
                                                        autoHideDuration: 3000,
                                                    });

                                                    removeSavingRow(rowId);
                                                    return oldRow;
                                                });
                                        }}
                                        hideFooterPagination
                                        hideFooterSelectedRowCount
                                        initialState={{
                                            density: 'compact'
                                        }}
                                        slots={{
                                            //pagination: CustomPagination,
                                        }}
                                        slotProps={{
                                            toolbar: {
                                                t: t,
                                            }
                                        }}
                                        disableSelectionOnClick
                                        localeText={muiGridLocales(language)}
                                    />
                                </Box>
                            </Grid>
                            {emailHistory.length > 0 &&
                                <Grid size={12}>
                                    <FormBoxTitle
                                        title={t('History')}
                                    />
                                    <Box sx={{width:'93%',ml:2, mt: 1}}>
                                        <DataGridPremium
                                            rows={emailHistory}
                                            columns={historyColumns}
                                            getRowId={(row) => row.MsgId}
                                            hideFooterPagination
                                            hideFooterSelectedRowCount
                                            initialState={{
                                                density: 'compact'
                                            }}
                                            disableSelectionOnClick
                                            localeText={muiGridLocales(language)}
                                        />
                                    </Box>
                                </Grid>
                            }
                        </Grid>
                    </Box>
                </PageContentWrapper>
            </Box>
        </SkeletonLoaderWrapper>
        <EmailPreviewDialog
            open={emailOpen}
            title={t('Send Email')}
            data={emailData}
            loading={loadingSendEmail}
            onSend={handleSendEmail}
            onCancel={handleCancelEmail}
            sendLabel={t('Send')}
            cancelLabel={t('Cancel')}
            toLabel={t('To')}
            ccLabel={t('Cc')}
            subjectLabel={t('Subject')}
            bodyLabel={t('Body')}
        />
        </>
    );
};

export default PoQuickView